import { ALCOHOL_USE_DISORDER_THERAPY_PROGRAM } from './programs/alcoholUseDisorder'
import { CLINICAL_LEAVE_EVALUATION_PROGRAM } from './programs/clinicalLeaveEvaluation'
import {
  CAREGIVER_TRAINING_FOR_NEURO_PROGRAM,
  COACHING_FOR_PARENTS_PROGRAM,
  COACHING_PROGRAM,
  MANAGER_COACHING_PROGRAM,
  NEURODIVERSITY_SKILLS_COACHING_PROGRAM,
  PERFORMANCE_COACHING_PROGRAM,
  SINGLE_SESSION_COACHING_PROGRAM,
} from './programs/coaching'
import { DIRECT_ACCESS_MEDS_PROGRAM, DIRECT_ACCESS_THERAPY_PROGRAM } from './programs/directAccess'
import {
  DIRECT_ACCESS_COUPLES_THERAPY_PROGRAM,
  DIRECT_ACCESS_FAMILY_THERAPY_PROGRAM,
  DIRECT_ACCESS_INDIVIDUAL_ADOLESCENT_THERAPY_PROGRAM,
  DIRECT_ACCESS_INDIVIDUAL_CHILD_THERAPY_PROGRAM,
  DIRECT_ACCESS_INDIVIDUAL_THERAPY_PROGRAM,
} from './programs/directAccessEngage'
import { ICAS_THERAPY_PROGRAM } from './programs/icas'
import { MEDICATION_MANAGEMENT_PROGRAM } from './programs/medicationManagement'
import { NEURO_PSYCH_EVALS_PROGRAM } from './programs/neuroPsychEvals'
import {
  BEHAVIORAL_HEALTH_URGENT_CARE_PROGRAM,
  PEDIATRIC_VIRTUAL_INTENSIVE_OUTPATIENT_PROGRAM,
} from './programs/pediatrics'
import { SELF_CARE_APPS_PROGRAM } from './programs/selfCareApps'
import {
  BLENDED_CARE_THERAPY_PROGRAM,
  DIALECTICAL_BEHAVIOR_THERAPY_PROGRAM,
  DIALECTICAL_BEHAVIOR_THERAPY_TEENS_PROGRAM,
  TEENS_THERAPY_PROGRAM,
} from './programs/therapy'
import { ProgramConfig } from './types'
import { ProgramNames } from '../../models/programs/Programs'

/**
 * Program configurations
 */
const ProgramConfigs = {
  [ProgramNames.BlendedCareTherapy]: BLENDED_CARE_THERAPY_PROGRAM,
  [ProgramNames.TeensTherapy]: TEENS_THERAPY_PROGRAM,
  [ProgramNames.DialecticalBehaviorTherapy]: DIALECTICAL_BEHAVIOR_THERAPY_PROGRAM,
  [ProgramNames.DialecticalBehaviorTherapyTeens]: DIALECTICAL_BEHAVIOR_THERAPY_TEENS_PROGRAM,

  [ProgramNames.Coaching]: COACHING_PROGRAM,
  [ProgramNames.CoachingForParents]: COACHING_FOR_PARENTS_PROGRAM,
  [ProgramNames.ManagerCoaching]: MANAGER_COACHING_PROGRAM,
  [ProgramNames.SingleSessionCoaching]: SINGLE_SESSION_COACHING_PROGRAM,
  [ProgramNames.PerformanceCoaching]: PERFORMANCE_COACHING_PROGRAM,
  [ProgramNames.NeurodiversitySkillsCoaching]: NEURODIVERSITY_SKILLS_COACHING_PROGRAM,
  [ProgramNames.CaregiverTrainingForNeuro]: CAREGIVER_TRAINING_FOR_NEURO_PROGRAM,
  [ProgramNames.NeuroPsychologicalEvaluations]: NEURO_PSYCH_EVALS_PROGRAM,
  [ProgramNames.BehavioralHealthUrgentCare]: BEHAVIORAL_HEALTH_URGENT_CARE_PROGRAM,
  [ProgramNames.PediatricVirtualIntensiveOutpatientProgram]: PEDIATRIC_VIRTUAL_INTENSIVE_OUTPATIENT_PROGRAM,

  [ProgramNames.DirectAccessIndividualTherapy]: DIRECT_ACCESS_INDIVIDUAL_THERAPY_PROGRAM,
  [ProgramNames.DirectAccessCouplesTherapy]: DIRECT_ACCESS_COUPLES_THERAPY_PROGRAM,
  [ProgramNames.DirectAccessFamilyTherapy]: DIRECT_ACCESS_FAMILY_THERAPY_PROGRAM,
  [ProgramNames.DirectAccessIndividualAdolescentTherapy]: DIRECT_ACCESS_INDIVIDUAL_ADOLESCENT_THERAPY_PROGRAM,
  [ProgramNames.DirectAccessIndividualChildTherapy]: DIRECT_ACCESS_INDIVIDUAL_CHILD_THERAPY_PROGRAM,

  [ProgramNames.AlcoholUseDisorderTherapy]: ALCOHOL_USE_DISORDER_THERAPY_PROGRAM,
  [ProgramNames.ClinicalLeaveEvaluation]: CLINICAL_LEAVE_EVALUATION_PROGRAM,
  [ProgramNames.MedicationManagement]: MEDICATION_MANAGEMENT_PROGRAM,

  // Non Lyra Care programs
  [ProgramNames.ICASTherapy]: ICAS_THERAPY_PROGRAM,
  [ProgramNames.DIRECT_ACCESS_MEDS]: DIRECT_ACCESS_MEDS_PROGRAM,
  [ProgramNames.DIRECT_ACCESS_THERAPY]: DIRECT_ACCESS_THERAPY_PROGRAM,
  [ProgramNames.SELF_CARE_APPS]: SELF_CARE_APPS_PROGRAM,
} satisfies Record<ProgramNames, ProgramConfig>

/**
 * Exports the program configs constants object directly to maintain compatibility with the previous PROGRAMS object,
 * and make it easy to access constant values (e.g. `PROGRAMS.ProgramName.customerPropertyId`).
 *
 * Prefer to use `Programs.getProgramConfigByName()` or `Programs.getProgramConfigById()` over indexing into PROGRAMS.
 *
 * You may get type errors in `PROGRAMS[foo]` when the index is a string and not a ProgramNames enum value,
 * or because the returned config object has a unioned type across programs that is too strict/specific.
 * `Programs.getProgramConfigByName()` accepts string and returns the looser ProgramConfig return type.
 */
export const PROGRAMS = ProgramConfigs

export namespace Programs {
  const programConfigById = Object.fromEntries(
    Object.values(ProgramConfigs)
      .filter((config) => config.blendedCareProgramId !== null)
      .map((config) => [config.blendedCareProgramId, config]),
  )

  export function getProgramConfigByName(name: ProgramNames): Readonly<ProgramConfig>
  export function getProgramConfigByName(name: string | undefined): Readonly<ProgramConfig> | undefined
  export function getProgramConfigByName(name: ProgramNames | string | undefined): Readonly<ProgramConfig> | undefined {
    return name ? ProgramConfigs[name as ProgramNames] : undefined
  }

  export function getProgramConfigById(programId: string | undefined | null): Readonly<ProgramConfig> | undefined {
    return programId ? programConfigById[programId] : undefined
  }

  export function getProgramsByCustomerPropertyId(customerPropertyId: string | undefined): Readonly<ProgramConfig>[] {
    return customerPropertyId
      ? getProgramConfigsMatching((config) => config.customerPropertyId === customerPropertyId)
      : []
  }

  export function getProgramConfigsMatching(
    predicate: (config: Readonly<ProgramConfig>) => boolean,
  ): Readonly<ProgramConfig>[] {
    return Object.values(ProgramConfigs).filter(predicate)
  }

  export function programSatisfies(
    name: ProgramNames,
    predicate: (config: Readonly<ProgramConfig>) => boolean,
  ): boolean {
    const config = getProgramConfigByName(name)
    return config !== undefined && predicate(config)
  }
}
